import { Pipe, PipeTransform } from '@angular/core';
import { PartialShipping } from '@pages/checkout-app/checkout-layout/main/components/settings/components/process-time/manage-process-time-dialog/manage-process-time-dialog.component';
import {
  describeCartValueDiscountBasis,
  LINKED_CALENDAR_DEFAULT_OPTION,
  RuleNameToDisplayNameMap,
} from '@pages/checkout-app/configs';
import { ShippingMethod, Shippings } from '@pages/checkout-app/types';
import { convertCondition, convertValues } from '@pages/checkout-app/utils';
import { Checkout } from '@pdqdev/pdq-shared/dist/interfaces';
import { ICheckoutExtensions, MultiShipmentExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { countryCodeToDisplayNameMap, extendedCountryToStates } from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { DropdownOption } from '@storybook/components/dropdown/dropdown.component';
import { FeeUnitTypeToDisplayName } from '../../components/fee-per-item/fee-per-item.component';

@Pipe({
  name: 'processingTimeOptions',
  standalone: false,
})
export class ProcessingTimeOptions implements PipeTransform {
  transform(shippings: Shippings[]): PartialShipping[] {
    return shippings.map((shipping) => ({
      id: shipping.id.toString(),
      name: shipping.name,
      disabled: shipping.shippingMethod?.category === ShippingMethod.LOCAL,
    }));
  }
}

@Pipe({
  name: 'processingCheckAll',
  standalone: false,
})
export class ProcessingTimeCheckAll implements PipeTransform {
  transform(selectedIdsLength: number, shippings: Shippings[]): 'hyper' | 'indeterminate' {
    const shippingsWithoutLocalMethods = shippings?.filter(
      (shipping) => shipping.shippingMethod?.category !== ShippingMethod.LOCAL,
    );
    if (shippingsWithoutLocalMethods?.length === selectedIdsLength) {
      return 'hyper';
    } else if (selectedIdsLength) {
      return 'indeterminate';
    }
  }
}

@Pipe({
  name: 'countriesDropdownOptions',
  standalone: false,
})
export class CountriesDropdownOptions implements PipeTransform {
  transform(countryCodes: string[], selectedCountryCodes: string[]): DropdownOption[] {
    const currentCountryCodes = selectedCountryCodes || [];
    return countryCodes.map((code) => ({
      value: code,
      label: countryCodeToDisplayNameMap[code],
      checked: currentCountryCodes.includes(code),
    }));
  }
}

@Pipe({
  name: 'statesDropdownOptions',
  standalone: false,
})
export class StatesDropdownOptions implements PipeTransform {
  transform(countryCode: string, selectedStateCodes: { [key: string]: string[] }): DropdownOption[] {
    const currentStateCodes = selectedStateCodes?.[countryCode] || [];
    const states = extendedCountryToStates[countryCode] || [];
    return states.map(({ label, value }: { label: string; value: string }) => ({
      value,
      label,
      checked: currentStateCodes.includes(value),
    }));
  }
}

@Pipe({
  name: 'dropdownOption',
  standalone: false,
})
export class DropdownOptionOf implements PipeTransform {
  transform(value: unknown, options: DropdownOption[]): DropdownOption | undefined {
    return options?.find((option) => option.value === value);
  }
}

@Pipe({
  name: 'feePerItemTypeOption',
  standalone: false,
})
export class FeePerItemTypeOption implements PipeTransform {
  transform(value: Checkout.FeeType, feeOptions: DropdownOption[]): DropdownOption {
    // Legacy 'usd' is charged as a fixed amount.
    const type = value === Checkout.FeeType.PERCENTAGE ? value : Checkout.FeeType.FIXED_AMOUNT;
    return feeOptions?.find((option) => option.value === type) ?? { value, label: '' };
  }
}

@Pipe({
  name: 'feePerItemstepUnitOption',
  standalone: false,
})
export class FeePerItemstepUnitOption implements PipeTransform {
  transform(value: Checkout.FeeUnitType): DropdownOption {
    const label = FeeUnitTypeToDisplayName.get(value);

    return {
      value,
      label,
    };
  }
}

@Pipe({
  name: 'displayRates',
  standalone: false,
})
export class DisplayRates implements PipeTransform {
  transform(shipping: Shippings, currency: string): string {
    if (
      shipping.shippingMethod?.category === ShippingMethod.DOMESTIC_LIVE_RATE ||
      shipping.shippingMethod?.category === ShippingMethod.INTERNATIONAL_LIVE_RATE
    ) {
      return 'Live rate';
    }

    const additionalRatesArray = shipping.additionalPriceRanges?.map((additionalRate) => +additionalRate.rate) ?? [];
    const rates = [+shipping.rate, ...additionalRatesArray];
    rates.sort((a, b) => a - b);
    const minPrice = rates[0];
    const maxPrice = rates[rates.length - 1];

    if (minPrice == maxPrice) {
      return `${minPrice == 0 ? 'Free' : currency + minPrice}`;
    }

    return `${minPrice == 0 ? 'Free' : currency + minPrice}-${currency + maxPrice}`;
  }
}

@Pipe({
  name: 'displayCategory',
  standalone: false,
})
export class DisplayCategory implements PipeTransform {
  transform(category: ShippingMethod): string {
    const categoryDisplayNameMap = {
      [ShippingMethod.DOMESTIC]: 'Domestic',
      [ShippingMethod.LOCAL]: 'Local',
      [ShippingMethod.DOMESTIC_LIVE_RATE]: 'Domestic live rate',
      [ShippingMethod.INTERNATIONAL_FIXED]: 'International fixed',
      [ShippingMethod.INTERNATIONAL_LIVE_RATE]: 'International live rate',
      [ShippingMethod.FIXED_LIVE_RATE]: 'Domestic fixed rate with live ETA',
      [ShippingMethod.INTERNATIONAL_FIXED_LIVE_RATE]: 'International fixed rate with live ETA',
      [ShippingMethod.STORE_PICK_UP]: 'Store pick up',
    };

    return categoryDisplayNameMap[category];
  }
}

@Pipe({
  name: 'filterLocationCondition',
  standalone: false,
})
export class FilterLocationCondition implements PipeTransform {
  transform(conditions: any[]) {
    return conditions.filter((condition) => condition?.name != 'locations');
  }
}

@Pipe({
  name: 'displayCondition',
  standalone: false,
})
export class DisplayCondition implements PipeTransform {
  transform(condition, currency): string {
    const nameValue = RuleNameToDisplayNameMap[condition?.name?.toLowerCase() ?? ''];
    const conditionValue = convertCondition(condition?.condition);
    const value = convertValues(condition, currency);

    const isCartValue = condition?.name === 'cartValue';
    const amount = isCartValue ? `${currency}${value}` : value;
    const discountBasis = isCartValue ? describeCartValueDiscountBasis(condition.optionalValue) : '';
    const weightUnit = condition?.weightUnit ? `- ${condition.weightUnit}` : '';

    return [nameValue, conditionValue, `${amount}${discountBasis}`, weightUnit].filter(Boolean).join(' ');
  }
}

@Pipe({
  name: 'displayPreviewRunningABTestOnlyIfExists',
  standalone: false,
})
export class DisplayPreviewRunningABTestOnlyIfExists implements PipeTransform {
  transform(options, runningTest) {
    if (runningTest) {
      return options;
    }
    return options.filter((o) => o.value != 'viewABTest');
  }
}

@Pipe({
  name: 'convertDatePickerExtensionsToLinkedOptions',
  standalone: false,
})
export class ConvertDatePickerExtensionsToLinkedOptions implements PipeTransform {
  transform(linkedDatePickerExtensions: ICheckoutExtensions[]): DropdownOption[] {
    if (linkedDatePickerExtensions?.length) {
      return linkedDatePickerExtensions.map((extension) => ({
        value: extension.id,
        label: extension.template.internalName,
        secondaryLabel: extension.template.title ?? 'No title',
      }));
    }
    return [LINKED_CALENDAR_DEFAULT_OPTION];
  }
}

@Pipe({
  name: 'convertMultiShipProfilesToLinkedOptions',
  standalone: false,
})
export class ConvertMultiShipProfilesToLinkedOptions implements PipeTransform {
  transform(multiShipExtensions: ICheckoutExtensions[]): DropdownOption[] {
    if (!multiShipExtensions?.length) {
      return [];
    }
    return multiShipExtensions.flatMap((extension) => {
      const template = extension.template as MultiShipmentExtension.Template;
      return (template?.datePickerProfiles ?? [])
        .filter((profile) => profile.enabled)
        .map((profile) => ({
          value: `${extension.id}:${profile.name}`,
          label: `${template.internalName}: ${profile.name}`,
        }));
    });
  }
}

@Pipe({
  name: 'sortByLabel',
  standalone: false,
})
export class SortByLabelPipe implements PipeTransform {
  transform(options: DropdownOption[]): DropdownOption[] {
    if (!options) return [];
    return [...options].sort((a, b) => a.label.localeCompare(b.label));
  }
}
